/**
 * AssetProcessDetail — the full "what process made this image" record for ONE
 * asset's step chain (capture v2 `asset-step`s): plan prompt + judge criteria,
 * every judged candidate with its per-criterion verdict (operator tier:
 * candidate images open via the signed-URL resolver), the improve-retry chain,
 * the ship decision, and uploads. ONE renderer for both sources — a build
 * trace's per-asset drill-in and a studio async job's trace (PR 4 entry).
 */
import { useState } from 'react';
import { useServices } from '../services/context.js';
import type { TraceStep } from './traceModel.js';

const fmtOffset = (t: number, start: number): string => `+${((t - start) / 1000).toFixed(1)}s`;

/** Open a private trace artifact (candidate image, prompt/response log)
 *  through the operator signed-URL resolver. */
function OpenArtifact({ gcsUri, label }: { gcsUri: string; label: string }) {
  const services = useServices();
  const [error, setError] = useState('');
  return (
    <>
      <button
        className="btn btn--sm"
        style={{ fontSize: 10.5, marginLeft: 6 }}
        onClick={() => {
          setError('');
          services.callable
            .resolveLlmPromptUrl({ gcsUri })
            .then(({ url }) => window.open(url, '_blank', 'noopener'))
            .catch(() => setError('operator-only'));
        }}
      >
        {label}
      </button>
      {error && (
        <span className="muted" style={{ fontSize: 10.5, marginLeft: 4 }}>
          {error}
        </span>
      )}
    </>
  );
}

function CriteriaChips({ failed, all }: { failed: string[]; all?: string[] }) {
  const names = all && all.length > 0 ? all : failed;
  if (names.length === 0) return null;
  return (
    <span>
      {names.map((c) => {
        const bad = failed.includes(c);
        return (
          <span
            key={c}
            style={{
              display: 'inline-block',
              fontSize: 10,
              padding: '0 5px',
              margin: '1px 3px 0 0',
              borderRadius: 8,
              border: `1px solid ${bad ? 'var(--danger, #e5484d)' : 'var(--ok, #30a46c)'}`,
              color: bad ? 'var(--danger, #e5484d)' : 'var(--ok, #30a46c)',
            }}
          >
            {c}
            {bad ? ' ✗' : ' ✓'}
          </span>
        );
      })}
    </span>
  );
}

/** One asset's chain, chronological. `redacted` renders the members' view
 *  note where operator detail was stripped server-side. */
export function AssetProcessDetail({
  chain,
  startedAt,
  redacted,
}: {
  chain: TraceStep[];
  startedAt: number;
  redacted: boolean;
}) {
  const planned = chain.find((s) => s.step === 'planned');
  const criteria = Array.isArray(planned?.criteria) ? (planned!.criteria as string[]) : [];
  return (
    <div data-testid="asset-process-detail" style={{ fontSize: 12 }}>
      {planned && (
        <div style={{ marginBottom: 6 }}>
          <div className="muted" style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            planned {typeof planned.kind === 'string' ? `· ${planned.kind}` : ''}{' '}
            {planned.hero === true ? '· hero' : ''}
          </div>
          {typeof planned.prompt === 'string' ? (
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11.5, margin: '2px 0' }}>{planned.prompt}</pre>
          ) : (
            redacted && <p className="muted" style={{ margin: '2px 0' }}>prompt — operator-only</p>
          )}
          {typeof planned.setupPrompt === 'string' && (
            <details>
              <summary className="muted" style={{ cursor: 'pointer', fontSize: 11 }}>
                setup prompt ({planned.setupPrompt.length} chars)
              </summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, margin: '2px 0' }}>{planned.setupPrompt}</pre>
            </details>
          )}
          {criteria.length > 0 && (
            <div className="muted" style={{ fontSize: 11 }}>judge criteria: {criteria.join(', ')}</div>
          )}
        </div>
      )}
      {chain
        .filter((s) => s.step !== 'planned')
        .map((s, i) => {
          const step = String(s.step);
          return (
            <div
              key={i}
              style={{
                borderLeft: '2px solid var(--border, #334)',
                paddingLeft: 8,
                margin: '4px 0',
              }}
            >
              <span className="muted" style={{ fontSize: 10.5, marginRight: 6 }}>
                {fmtOffset(s.t, startedAt)}
              </span>
              <strong style={{ fontSize: 11.5 }}>{step}</strong>
              {step === 'judged' && (
                <div style={{ margin: '2px 0' }}>
                  round {String(s.round)}:{' '}
                  {typeof s.infra === 'string' ? (
                    <span className="muted">judge unavailable ({s.infra}) — fail-open</span>
                  ) : s.pass === true ? (
                    <span style={{ color: 'var(--ok, #30a46c)' }}>PASS</span>
                  ) : (
                    <CriteriaChips failed={Array.isArray(s.failed) ? (s.failed as string[]) : []} all={criteria} />
                  )}
                  {typeof s.promptDelta === 'string' && s.promptDelta && (
                    <div className="muted" style={{ fontSize: 11 }}>suggested: “{s.promptDelta}”</div>
                  )}
                  {typeof s.imageGcsUri === 'string' && s.imageGcsUri && (
                    <OpenArtifact gcsUri={s.imageGcsUri} label="view candidate" />
                  )}
                </div>
              )}
              {step === 'retry-gen' && typeof s.prompt === 'string' && (
                <div className="muted" style={{ fontSize: 11 }}>
                  pass {String(s.pass)} retry prompt: “{s.prompt}”
                </div>
              )}
              {step === 'accepted' && (
                <span style={{ marginLeft: 6, color: 'var(--ok, #30a46c)' }}>{String(s.outcome)}</span>
              )}
              {step === 'banked' && (
                <span style={{ marginLeft: 6, color: 'var(--danger, #e5484d)' }}>
                  bank floor{Array.isArray(s.failed) ? ` (fails ${(s.failed as string[]).join(', ')})` : ''}
                </span>
              )}
              {step === 'uploaded' && typeof s.url === 'string' && (
                <div style={{ margin: '3px 0' }}>
                  <img
                    src={s.url}
                    alt={`variant ${String(s.variantId ?? s.slug ?? '')}`}
                    style={{ maxWidth: 180, maxHeight: 110, borderRadius: 6, display: 'block' }}
                  />
                  <a href={s.url} target="_blank" rel="noreferrer" className="muted" style={{ fontSize: 10.5 }}>
                    {String(s.variantId ?? s.url)}
                  </a>
                </div>
              )}
              {step === 'no-image' && typeof s.rawGcsUri === 'string' && s.rawGcsUri && (
                <OpenArtifact gcsUri={s.rawGcsUri} label="view raw (postprocess killed it)" />
              )}
              {step === 'upload-failed' && typeof s.error === 'string' && (
                <span className="muted" style={{ marginLeft: 6 }}>{s.error}</span>
              )}
            </div>
          );
        })}
      {redacted && (
        <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>
          Judge verdicts, candidate images, and composed prompts are operator-only.
        </p>
      )}
    </div>
  );
}
